import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';

const COMPACT_ENTER_SCROLL_THRESHOLD = 96;
const COMPACT_EXIT_SCROLL_THRESHOLD = 2;
const COMPACT_TRANSITION_SCROLL_GUARD_MS = 450;

export const resolveCompactHeaderState = (
  scrollY,
  isCompact,
  enterScrollThreshold = COMPACT_ENTER_SCROLL_THRESHOLD
) => (
  isCompact
    ? scrollY > COMPACT_EXIT_SCROLL_THRESHOLD
    : scrollY > enterScrollThreshold
);

export const shouldRestoreCompactScrollPosition = (
  scrollY,
  expectedScrollY,
  currentTime,
  guardUntil
) => (
  expectedScrollY > COMPACT_ENTER_SCROLL_THRESHOLD
  && scrollY <= COMPACT_EXIT_SCROLL_THRESHOLD
  && currentTime < guardUntil
);

const getCurrentTime = () => (
  typeof performance !== 'undefined' ? performance.now() : Date.now()
);

const restoreWindowScrollPosition = (scrollY) => {
  const rootElement = document.documentElement;
  const previousScrollBehavior = rootElement.style.scrollBehavior;

  rootElement.style.scrollBehavior = 'auto';
  window.scrollTo(0, scrollY);
  rootElement.style.scrollBehavior = previousScrollBehavior;
};

const StickyActionHeader = ({ children, className, desktopOffset = 'default' }) => {
  const [isCompact, setIsCompact] = useState(false);
  const compactStateRef = useRef(false);
  const compactScrollPositionRef = useRef(0);
  const compactScrollGuardUntilRef = useRef(0);
  const expandedHeaderHeightRef = useRef(0);
  const shellRef = useRef(null);
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const shellElement = shellRef.current;
    const headerElement = headerRef.current;
    if (!shellElement || !headerElement) return undefined;

    const preserveExpandedHeight = () => {
      if (compactStateRef.current) return;

      const expandedHeight = headerElement.getBoundingClientRect().height;
      if (expandedHeight > 0) {
        const roundedHeight = Math.ceil(expandedHeight);
        expandedHeaderHeightRef.current = roundedHeight;
        shellElement.style.height = `${roundedHeight}px`;
      }
    };

    preserveExpandedHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(preserveExpandedHeight)
      : null;
    resizeObserver?.observe(headerElement);
    window.addEventListener('resize', preserveExpandedHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', preserveExpandedHeight);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isCompact) return undefined;

    let animationFrame;
    let finalTransitionCheck;
    const preserveScrollPositionDuringTransition = () => {
      const currentTime = getCurrentTime();
      if (shouldRestoreCompactScrollPosition(
        window.scrollY,
        compactScrollPositionRef.current,
        currentTime,
        compactScrollGuardUntilRef.current
      )) {
        restoreWindowScrollPosition(compactScrollPositionRef.current);
      }
    };

    preserveScrollPositionDuringTransition();
    animationFrame = window.requestAnimationFrame(preserveScrollPositionDuringTransition);
    finalTransitionCheck = window.setTimeout(
      preserveScrollPositionDuringTransition,
      320
    );

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(finalTransitionCheck);
    };
  }, [isCompact]);

  useEffect(() => {
    let animationFrame;

    const updateCompactState = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const currentTime = getCurrentTime();
        if (shouldRestoreCompactScrollPosition(
          window.scrollY,
          compactScrollPositionRef.current,
          currentTime,
          compactScrollGuardUntilRef.current
        )) {
          restoreWindowScrollPosition(compactScrollPositionRef.current);
          return;
        }

        const enterScrollThreshold = Math.max(
          COMPACT_ENTER_SCROLL_THRESHOLD,
          expandedHeaderHeightRef.current + 32
        );
        const nextCompactState = resolveCompactHeaderState(
          window.scrollY,
          compactStateRef.current,
          enterScrollThreshold
        );

        if (nextCompactState !== compactStateRef.current) {
          if (nextCompactState) {
            compactScrollPositionRef.current = window.scrollY;
            compactScrollGuardUntilRef.current = currentTime + COMPACT_TRANSITION_SCROLL_GUARD_MS;

            if (expandedHeaderHeightRef.current > 0 && shellRef.current) {
              shellRef.current.style.height = `${expandedHeaderHeightRef.current}px`;
            }
          }

          compactStateRef.current = nextCompactState;
          setIsCompact(nextCompactState);
        }
      });
    };

    updateCompactState();
    window.addEventListener('scroll', updateCompactState, { passive: true });
    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', updateCompactState);
    };
  }, []);

  return (
    <div
      ref={shellRef}
      className={cn(
        'pointer-events-none sticky top-[calc(var(--mobile-app-header-height,4rem)+env(safe-area-inset-top))] z-30 -mx-1',
        desktopOffset === 'guest-menu' ? 'md:top-20' : 'md:top-0'
      )}
      data-compact={isCompact ? 'true' : 'false'}
    >
      <div
        ref={headerRef}
        className={cn(
          'pointer-events-auto flex w-full flex-row items-start justify-between gap-2 rounded-2xl border bg-background/95 p-3 shadow-sm backdrop-blur-md transition-[padding,gap] duration-300 ease-in-out sm:items-center',
          '[&>div:first-child]:min-w-0 [&>div:first-child]:flex-1',
          '[&>div:first-child>p]:max-h-12 [&>div:first-child>p]:overflow-hidden [&>div:first-child>p]:text-sm [&>div:first-child>p]:opacity-100 [&>div:first-child>p]:transition-[max-height,opacity,margin] [&>div:first-child>p]:duration-300 [&>div:first-child>p]:ease-out sm:[&>div:first-child>p]:text-base',
          '[&_h1]:transition-[font-size,line-height] [&_h1]:duration-300 [&_h1]:ease-out',
          '[&_h1]:text-xl sm:[&_h1]:text-2xl',
          '[&_[data-header-action-group]]:w-auto [&_[data-header-action-group]]:shrink-0 [&_[data-header-action-group]]:flex-nowrap [&_[data-header-action-group]]:items-center [&_[data-header-action-group]]:justify-end',
          '[&_[data-header-action]]:h-9 [&_[data-header-action]]:flex-shrink-0 [&_[data-header-action]]:overflow-hidden max-sm:[&_[data-header-action]]:min-w-9 max-sm:[&_[data-header-action]]:max-w-9 max-sm:[&_[data-header-action]]:gap-0 max-sm:[&_[data-header-action]]:px-0 max-sm:[&_[data-header-action]]:text-[0rem] sm:[&_[data-header-action]]:max-w-64 sm:[&_[data-header-action]]:text-sm',
          'max-sm:[&_[data-header-action]_svg]:m-0 [&_[data-header-action]_svg]:h-4 [&_[data-header-action]_svg]:w-4',
          '[&_[data-header-secondary]]:max-w-64 [&_[data-header-secondary]]:overflow-hidden [&_[data-header-secondary]]:opacity-100 [&_[data-header-secondary]]:transition-[max-width,opacity] [&_[data-header-secondary]]:duration-300 [&_[data-header-secondary]]:ease-out max-sm:[&_[data-header-secondary]]:hidden',
          isCompact && [
            'flex-row items-center gap-2 p-2',
            '[&>div:first-child]:min-w-0 [&>div:first-child]:flex-1 [&>div:first-child]:overflow-hidden',
            '[&>div:first-child>p]:mt-0 [&>div:first-child>p]:max-h-0 [&>div:first-child>p]:opacity-0',
            '[&_h1]:truncate [&_h1]:text-lg',
            '[&_[data-header-action-group]]:w-auto [&_[data-header-action-group]]:flex-shrink-0 [&_[data-header-action-group]]:flex-nowrap [&_[data-header-action-group]]:justify-end [&_[data-header-action-group]]:overflow-hidden',
            '[&_[data-header-actions]]:flex [&_[data-header-actions]]:flex-row [&_[data-header-actions]]:flex-nowrap [&_[data-header-actions]]:items-center',
            '[&_[data-header-secondary]]:pointer-events-none [&_[data-header-secondary]]:max-w-0 [&_[data-header-secondary]]:opacity-0',
            '[&_[data-header-action]]:h-9 [&_[data-header-action]]:min-w-9 [&_[data-header-action]]:max-w-9 [&_[data-header-action]]:flex-shrink-0 [&_[data-header-action]]:gap-0 [&_[data-header-action]]:px-0 [&_[data-header-action]]:text-[0rem]',
            '[&_[data-header-action]_svg]:m-0 [&_[data-header-action]_svg]:h-4 [&_[data-header-action]_svg]:w-4',
          ],
          className
        )}
      >
        {children}
      </div>
    </div>
  );
};

export default StickyActionHeader;
